import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Download, FileText, Video, Music, Image as ImageIcon, Code, File, Trash2, Eye, Heart, Check } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ResourceViewer } from "@/components/ResourceViewer";

const KIND_META = {
  pdf: { label: "PDF", bg: "#FAD4D4", Icon: FileText },
  video: { label: "Vidéo", bg: "#D4E2FA", Icon: Video },
  audio: { label: "Audio", bg: "#FCE5CD", Icon: Music },
  image: { label: "Image", bg: "#D4FADF", Icon: ImageIcon },
  html: { label: "HTML", bg: "#E5D4FA", Icon: Code },
  document: { label: "Document", bg: "#E8D7D0", Icon: File },
  autre: { label: "Fichier", bg: "#EFEAE6", Icon: File },
};

const formatSize = (bytes) => {
  if (!bytes) return "—";
  const units = ["o", "Ko", "Mo", "Go"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

export const ResourceCard = ({ resource, index = 0, isAdmin, onDeleted, onFavoriteChange, onViewed }) => {
  const [busy, setBusy] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [fav, setFav] = useState(!!resource.is_favorite);
  const [viewed, setViewed] = useState(!!resource.is_viewed);
  const meta = KIND_META[resource.kind] || KIND_META.autre;
  const { Icon } = meta;
  const previewable = ["pdf", "html", "image", "video", "audio"].includes(resource.kind);

  const toggleFav = async () => {
    const next = !fav;
    setFav(next);
    try {
      if (next) await api.post(`/favorites/${resource.id}`);
      else await api.delete(`/favorites/${resource.id}`);
      onFavoriteChange?.(resource.id, next);
      toast.success(next ? "Ajouté à vos favoris" : "Retiré de vos favoris");
    } catch {
      setFav(!next);
      toast.error("Action impossible");
    }
  };

  const markViewed = async () => {
    if (viewed) return;
    setViewed(true);
    try {
      await api.post(`/progress/${resource.id}`);
      onViewed?.(resource.id);
    } catch {
      setViewed(false);
    }
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/resources/${resource.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = resource.filename || "ressource";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      markViewed();
      toast.success("Téléchargement lancé");
    } catch {
      toast.error("Téléchargement impossible");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/resources/${resource.id}`);
      toast.success("Ressource supprimée");
      onDeleted?.(resource.id);
    } catch {
      toast.error("Suppression impossible");
    }
  };

  return (
    <motion.article
      data-testid={`resource-card-${resource.id}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.06, 0.5) }}
      className="group bg-white border border-[#E8D7D0] rounded-3xl p-7 flex flex-col hover:-translate-y-1 transition-transform duration-300 ease-out hover:shadow-[0_8px_30px_rgba(183,110,121,0.12)]"
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <span
          className="rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#2C2627]"
          style={{ backgroundColor: meta.bg }}
        >
          {meta.label}
        </span>
        <div className="flex items-center gap-2">
          <button
            data-testid={`favorite-toggle-${resource.id}`}
            aria-label="Favori"
            onClick={toggleFav}
            className="rounded-full p-2 hover:bg-[#F5E6E8] transition-colors duration-300"
          >
            <Heart
              strokeWidth={1.5}
              className={`w-5 h-5 ${fav ? "text-[#B76E79] fill-[#B76E79]" : "text-[#5E5356]"}`}
            />
          </button>
          <Icon strokeWidth={1.5} className="w-6 h-6 text-[#B76E79]" />
        </div>
      </div>

      <p className="text-[11px] uppercase tracking-[0.22em] text-[#B76E79] mb-2">
        {resource.category}
        {resource.subcategory ? ` · ${resource.subcategory}` : ""}
        {resource.chapter ? ` · ${resource.chapter}` : ""}
      </p>
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="font-display text-2xl leading-tight text-[#2C2627]">{resource.title}</h3>
        {viewed && (
          <span
            data-testid={`viewed-badge-${resource.id}`}
            className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#D4FADF] px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#2C2627]"
          >
            <Check strokeWidth={2} className="w-3 h-3" />
            Consulté
          </span>
        )}
      </div>
      {resource.description && (
        <p className="text-sm text-[#5E5356] leading-relaxed mb-6 line-clamp-3">{resource.description}</p>
      )}

      <div className="mt-auto flex items-center justify-between gap-3 pt-4 border-t border-[#F0E4E0]">
        <span className="text-xs text-[#5E5356]">
          {formatSize(resource.size)} · {resource.downloads} téléch.
        </span>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon"
              data-testid={`delete-resource-${resource.id}`}
              onClick={handleDelete}
              className="rounded-full text-[#5E5356] hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 strokeWidth={1.5} className="w-4 h-4" />
            </Button>
          )}
          {previewable && (
            <Button
              variant="outline"
              data-testid={`preview-resource-${resource.id}`}
              onClick={() => {
                setViewerOpen(true);
                markViewed();
              }}
              className="rounded-full border-[#B76E79] text-[#B76E79] hover:bg-[#F5E6E8]"
            >
              <Eye strokeWidth={1.5} className="w-4 h-4 mr-2" />
              Lire
            </Button>
          )}
          <Button
            data-testid={`download-resource-${resource.id}`}
            disabled={busy}
            onClick={handleDownload}
            className="rounded-full bg-[#B76E79] hover:bg-[#A25D67] text-white"
          >
            <Download strokeWidth={1.5} className="w-4 h-4 mr-2" />
            {busy ? "..." : "Télécharger"}
          </Button>
        </div>
      </div>

      <ResourceViewer resource={resource} open={viewerOpen} onOpenChange={setViewerOpen} />
    </motion.article>
  );
};
